/*
 * pdp-section-nav.js
 * ---------------------------------------------------------------
 * Anchor navigation for the product page tab bar.
 *
 * Three jobs:
 *   1. Resolve each tab to a real element, and hide tabs whose section
 *      is not on the page yet.
 *   2. Scroll to it, offset by whatever is pinned above.
 *   3. Mark the tab for the section currently being read.
 *
 * The links work without any of this -- they are real anchors with real
 * hrefs, so a failed script degrades to a plain jump, not a dead bar.
 *
 * Vanilla ES6, no dependencies.
 * ---------------------------------------------------------------
 */

(function () {
  'use strict';

  /*
   * A Shopify section's DOM id is prefixed with the template it sits in:
   *   shopify-section-template--23230253072640__product_faq_cro
   * That prefix changes when the theme or template is duplicated, so the
   * short key is matched against the end of the id instead of the whole
   * thing. Tried in order: an exact id, an explicit data-pdp-anchor, then
   * the section-id suffix.
   */
  function resolveTarget(key) {
    if (!key) return null;

    var byId = document.getElementById(key);
    if (byId) return byId;

    var byAnchor = document.querySelector('[data-pdp-anchor="' + CSS.escape(key) + '"]');
    if (byAnchor) return byAnchor;

    var sections = document.querySelectorAll('[id^="shopify-section-"]');
    for (var i = 0; i < sections.length; i++) {
      var id = sections[i].id;
      if (id === 'shopify-section-' + key || id.indexOf('__' + key) === id.length - key.length - 2) {
        return sections[i];
      }
    }
    return null;
  }

  function setup(nav) {
    var links = Array.from(nav.querySelectorAll('[data-pdp-nav-link]'));
    if (!links.length) return;

    var list = nav.querySelector('[data-pdp-nav-list]');
    var entries = [];

    links.forEach(function (link) {
      var target = resolveTarget(link.getAttribute('data-target'));
      if (!target) {
        // Section not built yet — hide the tab rather than offer a dead link.
        link.parentElement.hidden = true;
        return;
      }
      link.parentElement.hidden = false;
      entries.push({ link: link, target: target });
    });

    if (!entries.length) {
      nav.hidden = true;
      return;
    }
    nav.hidden = false;

    /*
     * How much is pinned above the target. The theme header changes
     * height between breakpoints and shrinks on scroll, so this is
     * measured each time rather than stored.
     */
    function stickyOffset() {
      var total = 0;

      var header = document.querySelector(
        '.section-header, .shopify-section-header-sticky, header.header-wrapper'
      );
      if (header) {
        var hPos = getComputedStyle(header).position;
        if (hPos === 'sticky' || hPos === 'fixed') total += header.offsetHeight;
      }

      var navPos = getComputedStyle(nav).position;
      if (navPos === 'sticky' || navPos === 'fixed') total += nav.offsetHeight;

      return total;
    }

    // Keeps the bar clear of a sticky header rather than sliding under it.
    function themeHeader() {
      return document.querySelector(
        '.section-header, .shopify-section-header-sticky, header.header-wrapper'
      );
    }

    function positionNav() {
      var header = themeHeader();
      var top = 0;
      if (header) {
        var hPos = getComputedStyle(header).position;
        if (hPos === 'sticky' || hPos === 'fixed') top = header.offsetHeight;
      }
      nav.style.setProperty('--pdp-nav-top', top + 'px');
    }

    /*
     * Once the bar reaches the header it pins directly beneath it, and the
     * header is held on screen for as long as the bar is pinned -- so the
     * menu, search and cart stay one tap away while reading the sections
     * the tabs are for. The header normally hides on the way down; the
     * hold is what stops that (see `hide()` in sections/header.liquid).
     * Above the bar the header behaves exactly as it did.
     *
     * The anchor is a zero-height marker where the bar sits in the flow.
     * Measuring the bar itself would be circular -- it stops moving the
     * moment it pins, so its own position can no longer tell us whether
     * it should be pinned.
     */
    var marker = document.createElement('span');
    marker.className = 'pdp-nav__marker';
    marker.setAttribute('aria-hidden', 'true');
    nav.parentNode.insertBefore(marker, nav);

    var pinned = false;
    function syncPinned() {
      var header = themeHeader();
      var offset = header ? header.offsetHeight : 0;
      // One threshold both ways: the bar pins where the header ends and
      // unpins as soon as its place in the flow comes back below that.
      var should = marker.getBoundingClientRect().top <= offset;
      if (should === pinned) return;
      pinned = should;
      // Height is read before pinning, while the bar is still in flow.
      if (pinned) marker.style.setProperty('--pdp-nav-height', nav.offsetHeight + 'px');
      if (header) {
        header.classList.toggle('pdp-header-hold', pinned);
        if (pinned) {
          // Bring it back if it had slid away on the way down. `animate`
          // is the theme's own transition on `top`, so it slides in.
          header.classList.add('shopify-section-header-sticky', 'animate');
          header.classList.remove('shopify-section-header-hidden');
        }
      }
      positionNav();
      nav.classList.toggle('is-pinned', pinned);
      marker.classList.toggle('is-holding', pinned);
    }

    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /*
     * While a tap's smooth scroll is running, the page passes over every
     * section between here and there -- and the scroll handler marked
     * each one in turn, so the indicator raced across the bar before
     * landing. The chosen tab is held until the scroll settles.
     *
     * Released by the scroll stopping rather than by a fixed timer: how
     * long a smooth scroll takes depends on the distance and the
     * browser. The timer is only a backstop for the case where the page
     * cannot move at all -- a tab already in view -- which fires no
     * scroll events to settle.
     */
    var lockedLink = null;
    var lockTimer = null;

    function lockTo(link) {
      lockedLink = link;
      window.clearTimeout(lockTimer);
      lockTimer = window.setTimeout(releaseLock, 1200);
    }

    function releaseLock() {
      window.clearTimeout(lockTimer);
      lockedLink = null;
    }

    var settleTimer = null;
    window.addEventListener(
      'scroll',
      function () {
        if (!lockedLink) return;
        window.clearTimeout(settleTimer);
        settleTimer = window.setTimeout(releaseLock, 140);
      },
      { passive: true }
    );

    entries.forEach(function (entry) {
      entry.link.addEventListener('click', function (event) {
        event.preventDefault();
        var top =
          window.pageYOffset +
          entry.target.getBoundingClientRect().top -
          stickyOffset() -
          8; // a little air above the heading

        lockTo(entry.link);
        setActive(entry.link);

        window.scrollTo({ top: Math.max(0, top), behavior: reduced ? 'auto' : 'smooth' });

        // Focus follows the scroll, so a keyboard user lands in the
        // section rather than staying up in the bar.
        entry.target.setAttribute('tabindex', '-1');
        entry.target.focus({ preventScroll: true });
      });
    });

    var indicator = nav.querySelector('[data-pdp-nav-indicator]');

    // The bar is measured against the list, not the viewport, so it stays
    // correct while the list itself is scrolled sideways.
    function moveIndicator(link) {
      if (!indicator || !link) return;
      indicator.style.width = link.offsetWidth + 'px';
      indicator.style.transform = 'translateX(' + link.offsetLeft + 'px)';
      indicator.classList.add('is-ready');
    }

    /*
     * The tab that is currently marked. Held here because `update()`
     * runs on every animation frame while scrolling, and everything
     * below is only worth doing when the answer has actually changed.
     *
     * This is the bug that pushed the row sideways: `scrollBy` was
     * being called again on every frame, each call measuring a list
     * that was still moving from the last one, so the offsets compounded
     * and the first tab walked off to the left.
     */
    var activeLink = null;

    function setActive(link, force) {
      if (!link) return;
      if (link === activeLink && !force) return;
      activeLink = link;

      entries.forEach(function (e) {
        var on = e.link === link;
        e.link.classList.toggle('is-active', on);
        if (on) {
          e.link.setAttribute('aria-current', 'true');
        } else {
          e.link.removeAttribute('aria-current');
        }
      });

      moveIndicator(link);
      revealTab(link);
    }

    /*
     * On a phone the bar scrolls sideways; keep the active tab in view.
     * An absolute target rather than a relative nudge, so a call that
     * lands mid-animation corrects to the same place instead of adding
     * to whatever the last one asked for.
     */
    function revealTab(link) {
      if (!list || list.scrollWidth <= list.clientWidth + 1) return;

      var target = link.offsetLeft - (list.clientWidth - link.offsetWidth) / 2;
      var max = list.scrollWidth - list.clientWidth;
      target = Math.max(0, Math.min(target, max));

      // Nothing to do for a shift smaller than a couple of pixels --
      // scheduling one is what makes a row look restless.
      if (Math.abs(target - list.scrollLeft) < 2) return;

      list.scrollTo({ left: target, behavior: reduced ? 'auto' : 'smooth' });
    }

    /*
     * The active tab is the last section whose top has passed the line
     * where content becomes readable. Simpler and steadier than an
     * IntersectionObserver here, because sections vary hugely in height
     * and several can be on screen at once.
     */
    var ticking = false;
    function update() {
      ticking = false;
      if (lockedLink) return;
      var line = stickyOffset() + 24;
      var current = null;

      entries.forEach(function (e) {
        if (e.target.getBoundingClientRect().top <= line) current = e.link;
      });

      // Past the bottom of the page the last section is the one being read.
      if (window.innerHeight + window.pageYOffset >= document.body.scrollHeight - 4) {
        current = entries[entries.length - 1].link;
      }

      /*
       * Above the first section nothing has passed the line, so this was
       * null -- which cleared every tab and left the indicator stranded
       * under whichever one it happened to be beneath. Scrolling back to
       * the top is a normal thing to do; the first tab is the honest
       * answer there.
       */
      if (!current) current = entries[0].link;

      setActive(current);
    }

    function onScroll() {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(function () {
          syncPinned();
          update();
        });
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });

    window.addEventListener('resize', function () {
      // The bar's height changes when the labels rewrap, and the marker
      // holds that height while the bar is pinned. Stale, the page jumps
      // by the difference the next time it unpins.
      if (pinned) marker.style.setProperty('--pdp-nav-height', nav.offsetHeight + 'px');
      positionNav();
      // Label widths change with the viewport, so the indicator is
      // re-measured rather than left where it was.
      if (activeLink) setActive(activeLink, true);
      onScroll();
    });

    // Web fonts land after first paint and change every label's width,
    // so the bar is measured again once they have.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () {
        if (activeLink) setActive(activeLink, true);
      });
    }

    positionNav();
    syncPinned();
    update();
  }

  function init() {
    document.querySelectorAll('[data-pdp-nav]').forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // The theme editor re-renders sections without a page load.
  document.addEventListener('shopify:section:load', init);
})();
